<script setup>
/**
 * The floating click-to-edit popover the preview opens next to whichever
 * element was clicked. One shell for every element type — the caller slots
 * the matching editor in. Edits apply live to the same state the rail
 * binds, so "Done" (and Esc / click-outside) only closes.
 *
 * Positioning: preferred to the left of the anchor (the preview card hugs
 * the canvas's right edge), flipping to the right when there's no room,
 * clamped to the viewport, and re-computed on scroll/resize/content growth.
 */
import { nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  onClickOutside,
  useEventListener,
  useResizeObserver,
} from '@vueuse/core';

import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  /** The preview DOM element the popover is anchored beside. */
  anchorEl: {
    type: Object,
    default: null,
  },
  title: {
    type: String,
    required: true,
  },
});

const emit = defineEmits(['close']);

const { t } = useI18n();

const POPOVER_WIDTH = 320;
const VIEWPORT_GAP = 12;

const popoverRef = ref(null);
const position = ref({ top: 0, left: 0 });

const updatePosition = () => {
  const rect = props.anchorEl?.getBoundingClientRect?.();
  if (!rect || !popoverRef.value) return;

  let left = rect.left - POPOVER_WIDTH - VIEWPORT_GAP;
  if (left < VIEWPORT_GAP) {
    left = Math.min(
      rect.right + VIEWPORT_GAP,
      window.innerWidth - POPOVER_WIDTH - VIEWPORT_GAP
    );
  }

  const height = popoverRef.value.offsetHeight;
  const top = Math.min(
    Math.max(rect.top, VIEWPORT_GAP),
    Math.max(window.innerHeight - height - VIEWPORT_GAP, VIEWPORT_GAP)
  );

  position.value = { top, left };
};

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([type="file"]):not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

const focusableItems = () => [
  ...(popoverRef.value?.querySelectorAll(FOCUSABLE) ?? []),
];

const focusFirstField = () => {
  popoverRef.value
    ?.querySelector('input:not([type="file"]), textarea, select')
    ?.focus();
};

// The popover is teleported to the end of <body>, so without this the trigger
// in the preview loses its place: Tab would walk out of the document and
// closing would drop focus onto <body>. Keep Tab inside while it is open and
// hand focus back to whatever opened it.
let previouslyFocused = null;

const handleTab = event => {
  const items = focusableItems();
  if (!items.length) return;

  const first = items[0];
  const last = items[items.length - 1];
  const active = document.activeElement;

  if (
    event.shiftKey &&
    (active === first || !popoverRef.value?.contains(active))
  ) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && active === last) {
    event.preventDefault();
    first.focus();
  }
};

onMounted(() => {
  previouslyFocused = document.activeElement;
  updatePosition();
  focusFirstField();
});

onBeforeUnmount(() => previouslyFocused?.focus?.());

// Re-anchoring on another element repositions and re-focuses in place —
// the popover instance is keyed by the page per target, but anchor moves
// (device toggle re-layout) still land here.
watch(
  () => props.anchorEl,
  () => nextTick(updatePosition)
);

useResizeObserver(popoverRef, updatePosition);
useEventListener(window, 'resize', updatePosition);
useEventListener(window, 'scroll', updatePosition, { capture: true });
useEventListener(document, 'keydown', event => {
  if (event.key === 'Escape') emit('close');
  if (event.key === 'Tab') handleTab(event);
});
onClickOutside(popoverRef, () => emit('close'));
</script>

<template>
  <Teleport to="body">
    <div
      ref="popoverRef"
      role="dialog"
      :aria-label="title"
      class="fixed z-50 w-80 max-h-[calc(100vh-24px)] overflow-y-auto flex flex-col gap-3 p-4 rounded-xl shadow-xl bg-n-solid-1 outline outline-1 -outline-offset-1 outline-n-container"
      :style="{ top: `${position.top}px`, left: `${position.left}px` }"
    >
      <div class="flex items-center justify-between gap-2">
        <span class="text-heading-3 text-n-slate-12">{{ title }}</span>
        <Button
          variant="ghost"
          color="slate"
          size="xs"
          :label="t('CAMPAIGN.BUILDER.POPOVER.DONE')"
          @click="emit('close')"
        />
      </div>

      <slot />
    </div>
  </Teleport>
</template>
